import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface PaginationProps {
  /** 1-based current page */
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Number of sibling pages to show on each side of the current page */
  siblingCount?: number;
  className?: string;
  'data-testid'?: string;
}

const DOTS = 'dots' as const;

type PageItem = number | typeof DOTS;

function range(start: number, end: number): number[] {
  const length = Math.max(end - start + 1, 0);
  return Array.from({ length }, (_, i) => start + i);
}

/**
 * Builds the list of page items to render, inserting ellipsis (`DOTS`) where
 * pages are collapsed. Always shows the first and last page.
 */
function getPageItems(currentPage: number, totalPages: number, siblingCount: number): PageItem[] {
  // First + last + current + 2*siblings + 2 dots
  const totalPageNumbers = siblingCount * 2 + 5;

  if (totalPageNumbers >= totalPages) {
    return range(1, totalPages);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  const showLeftDots = leftSibling > 2;
  const showRightDots = rightSibling < totalPages - 1;

  if (!showLeftDots && showRightDots) {
    const leftItemCount = 3 + 2 * siblingCount;
    return [...range(1, leftItemCount), DOTS, totalPages];
  }

  if (showLeftDots && !showRightDots) {
    const rightItemCount = 3 + 2 * siblingCount;
    return [1, DOTS, ...range(totalPages - rightItemCount + 1, totalPages)];
  }

  return [1, DOTS, ...range(leftSibling, rightSibling), DOTS, totalPages];
}

const arrowBtn =
  'inline-flex items-center justify-center min-h-[36px] min-w-[36px] px-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  className = '',
  'data-testid': testId = 'pagination',
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const items = getPageItems(currentPage, totalPages, siblingCount);
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const goTo = (page: number) => {
    const clamped = Math.min(Math.max(page, 1), totalPages);
    if (clamped !== currentPage) onPageChange(clamped);
  };

  return (
    <nav
      role="navigation"
      aria-label="Pagination"
      data-testid={testId}
      className={`flex items-center gap-1 ${className}`}
    >
      <button
        type="button"
        onClick={() => goTo(1)}
        disabled={!canPrev}
        aria-label="First page"
        data-testid="pagination-btn-first"
        className={arrowBtn}
      >
        <ChevronsLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => goTo(currentPage - 1)}
        disabled={!canPrev}
        aria-label="Previous page"
        data-testid="pagination-btn-prev"
        className={arrowBtn}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {items.map((item, idx) =>
        item === DOTS ? (
          <span
            key={`dots-${idx}`}
            aria-hidden
            className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] px-1 text-gray-400 select-none"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => goTo(item)}
            aria-current={item === currentPage ? 'page' : undefined}
            data-testid={`pagination-page-${item}`}
            className={`inline-flex items-center justify-center min-h-[36px] min-w-[36px] px-2 rounded-lg border text-sm font-medium transition-colors ${
              item === currentPage
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {item}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => goTo(currentPage + 1)}
        disabled={!canNext}
        aria-label="Next page"
        data-testid="pagination-btn-next"
        className={arrowBtn}
      >
        <ChevronRight className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => goTo(totalPages)}
        disabled={!canNext}
        aria-label="Last page"
        data-testid="pagination-btn-last"
        className={arrowBtn}
      >
        <ChevronsRight className="w-4 h-4" />
      </button>
    </nav>
  );
}
